<template>
  <section id="features" class="company-features-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">企业特色</h2>
        <div class="title-underline"></div>
        <p class="section-description">
          我们以创新为核心，以患者为中心，提供全方位的现代化医疗服务。
        </p>
      </div>

      <div class="features-grid">
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="feature-card"
          v-motion
          :initial="{ opacity: 0, y: 30 }"
          :visible="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: index * 100 }"
        >
          <div class="feature-icon-container">
            <img :src="require(`@/assets/svg/${feature.icon}`)" :alt="feature.title" class="feature-svg-icon">
          </div>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const features = [
  {
    icon: 'research_center.svg',
    title: '前沿研究',
    description: '我们积极参与国际前沿医学研究，推动医疗技术发展，为患者提供最新的治疗方案。'
  },
  {
    icon: 'smartHealthcare.svg',
    title: '智能医疗',
    description: '运用人工智能和大数据技术，提升诊断准确率，优化治疗方案，实现精准医疗。'
  },
  {
    icon: 'clinic.svg',
    title: '专家团队',
    description: '汇聚国内外知名医疗专家，组建多学科协作团队，为患者提供权威诊疗服务。'
  },
  {
    icon: 'precisionMedicine.svg',
    title: '个性化治疗',
    description: '根据每位患者的具体情况，制定个性化治疗方案，确保最佳治疗效果。'
  },
  {
    icon: 'international.svg',
    title: '国际合作',
    description: '与国际知名医疗机构建立合作关系，引进先进技术和管理经验。'
  },
  {
    icon: 'qcga.svg',
    title: '人文关怀',
    description: '注重患者的心理和情感需求，提供温馨舒适的就医环境和贴心服务。'
  }
]
</script>

<style scoped>
.company-features-section {
  padding: 3rem 0; /* 减少移动端的上下内边距 */
  background-color: #f9fafb;
}

.container {
  width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box; /* 确保padding不会增加总宽度 */
}

/* 响应式容器内边距 */
@media (min-width: 640px) {
  .container {
    padding: 0 1.5rem;
  }
}

@media (min-width: 1024px) {
  .container {
    max-width: 1280px;
    padding: 0 2rem;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 2rem; /* 减少移动端的底部间距 */
  padding: 0 0.5rem; /* 增加小屏幕的左右内边距 */
}

.section-title {
  font-size: 1.5rem; /* 减小移动端标题大小 */
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
  line-height: 1.2; /* 优化行高 */
}

/* 响应式标题大小 */
@media (min-width: 640px) {
  .section-title {
    font-size: 1.875rem;
  }
}

@media (min-width: 768px) {
  .section-title {
    font-size: 2.25rem;
  }
}

.title-underline {
  width: 4rem; /* 减小下划线长度 */
  height: 0.25rem;
  background-color: #2563eb;
  margin: 0 auto 1rem; /* 增加下划线与描述的间距 */
  border-radius: 9999px;
}

.section-description {
  color: #4b5563;
  max-width: 42rem;
  margin-left: auto;
  margin-right: auto;
  font-size: 1rem; /* 减小移动端描述文字大小 */
  line-height: 1.6; /* 优化行高 */
}

/* 响应式网格布局 */
.features-grid {
  display: grid;
  grid-template-columns: 1fr; /* 移动端单列 */
  gap: 1.5rem; /* 减小移动端卡片间距 */
}

/* 平板设备双列布局 */
@media (min-width: 640px) {
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
}

/* 桌面设备三列布局 */
@media (min-width: 1024px) {
  .features-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 2.5rem;
  }
}

.feature-card {
  background-color: white;
  border-radius: 1rem;
  padding: 1.5rem; /* 减小移动端内边距 */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease; /* 增加所有属性的过渡效果 */
  cursor: pointer; /* 提示可交互 */
}

/* 增加触摸反馈 */
.feature-card:hover, .feature-card:focus {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  transform: translateY(-3px); /* 轻微上浮效果 */
}

.feature-icon-container {
  background-color: #dbeafe;
  width: 3.5rem; /* 减小移动端图标容器 */
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem; /* 减小间距 */
}

.feature-svg-icon {
  width: 1.75rem; /* 减小移动端图标 */
  height: 1.75rem;
}

.feature-title {
  font-size: 1.15rem; /* 减小移动端标题 */
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
}

.feature-description {
  color: #4b5563;
  line-height: 1.6;
  font-size: 0.95rem; /* 减小移动端描述文字 */
}

/* 针对小屏幕手机的额外优化 */
@media (max-width: 360px) {
  .company-features-section {
    padding: 2rem 0;
  }
  
  .section-title {
    font-size: 1.35rem;
  }
  
  .feature-card {
    padding: 1.25rem;
  }
}
</style>
